import { useMemo, useState } from 'react';
import { Icon, IconName } from '../ui/Icon';
import { ComponentReplacement } from '../ComponentReplacement';
import { useTranslate } from '~/hooks/useTranslate';
import clsx from 'clsx';

export const InputFileAttachment = () =>{
  const t = useTranslate();
  const [ files, setFiles ] = useState<Array<FileList | null>>( [null,null,null,null] );

  const totalFileSize = useMemo( () => {
    return files.reduce( ( size, file ) => {
      if ( file ) {
        size += ( file?.[0]?.size ?? 0 ) ?? 0;
      }

      return size;
    }, 0 )
  }, [ files ] );

  const exceededTotalFileSize = useMemo( () => {
    return ( totalFileSize / 1024 / 1024 ) > 20;
  }, [ totalFileSize ] );

  return (
    <div className={ 'flex flex-col-reverse gap-3' }>
      { files.map( ( _, index ) => {
        if ( !( files?.[ index - 1 ] != null ) && index !== 0 ) {
          return null;
        }

        const file = files[index]?.[0];
        const isToLarge = ( ( file?.size ?? 0 ) / 1024 / 1024 ) > 5;
        if ( file == null && exceededTotalFileSize ) {
          return null;
        }

        return (  
          <div
            key={ index }
            className={ clsx(
              'border border-white/30 bg-[#17193F4D] rounded-lg relative  flex items-center justify-center',
              {
                'h-[108px] border-dashed': file == null,
                'cursor-pointer': file != null
              }
            ) }>
            { !isToLarge &&
              <input
                key={ index }
                accept={
                  `image/jpeg,
                  image/png,
                  image/gif,
                  image/bmp,
                  image/tiff,
                  image/heic,
                  image/heif,
                  image/webp,
                  image/svg+xml,
                  application/pdf,
                  text/plain,
                  application/rtf,
                  application/vnd.apple.pages,
                  application/vnd.apple.numbers,
                  application/vnd.apple.keynote,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                  application/vnd.openxmlformats-officedocument.presentationml.presentation,
                  application/vnd.oasis.opendocument.text,
                  application/vnd.oasis.opendocument.spreadsheet,
                  application/vnd.oasis.opendocument.presentation,
                  video/mp4,
                  video/x-msvideo,
                  video/quicktime,
                  video/x-matroska,
                  video/webm`
                }
                name={ file != null ? `file_${index}` : undefined }
                className={ clsx(
                  'absolute inset-0 opacity-0',
                  {
                    'cursor-pointer': file == null,
                    'pointer-events-none cursor-default': file != null
                  }
                ) }
                type="file" 
                onChange={ ( e ) => {
                  const newFiles = [...files];
                  newFiles[ index ] = e.currentTarget.files;

                  setFiles( newFiles );
                } }
              />
            }
            { file == null &&
              <div className={ 'flex flex-col items-center gap-2.5' }>
                <Icon
                  name={ IconName.FileArrowUpAlt_1 }
                  className={ 'icon-size-5' } />
                <div className={ 'flex flex-col items-center gap-1.5' }>
                  <p className={ 'text-center text-sm font-medium' }>
                    <ComponentReplacement componentReplacements={ {
                      clickhere: () => (
                        <span className={ 'underline underline-offset-2' }>{t( 'SUPPORT_FILEUPLOAD_CLICKHERE' )}</span>
                      )
                    } }>
                      { t( 'SUPPORT_FILEUPLOAD_CLICK' )}
                    </ComponentReplacement>
                  </p>
                  <p className={ 'text-center text-xs' }>{t( 'SUPPORT_FILEUPLOAD_SUPPORTED_FORMAT' )}</p>
                </div>
              </div>
            }
            { file != null &&
              <div className={ 'flex flex-row w-full p-3 gap-2' }>
                <Icon
                  name={ isToLarge ? IconName.FileXmarkAlt_1 : IconName.FileCheckAlt_1 }
                  className={ 'icon-size-5 self-start' } />
                <div className={ 'flex-auto flex flex-col gap-2' }>
                  <div className={ 'text-sm font-medium' }>{file.name}</div>
                  { isToLarge &&
                    <div className={ 'text-[#D94848] text-xs' }>{t( 'SUPPORT_FILEUPLOAD_FILE_TO_LARGE' )}</div>
                  }
                </div>
                <button
                  type="button"
                  className={ 'flex items-center justify-center cursor-pointer' }
                  onClick={ () => {
                    const newFiles = [...files];
          
                    newFiles.splice( index, 1 );
                    newFiles.push( null );
          
                    setFiles( newFiles );
                  } }>
                  <Icon
                    name={ IconName.Trash }
                    className={ 'icon-size-5' } />
                </button>
              </div>
            }
          </div>
        )
      } ) }
    </div>
  )
}
